/**
 * Multipart upload with progress. The shared client is fetch-based (it also runs under Bun, where
 * XMLHttpRequest does not exist), so the browser-only progress path lives here. Field name is
 * `file`, exactly as docs/CONTRACT.md §4 specifies, and Content-Type is left to the FormData
 * boundary.
 */
import { ApiClientError, type ApiErrorCode } from '@olmt/shared'
import { api } from './api'

const ERROR_CODES: Record<string, true> = {
  unauthorized: true,
  forbidden: true,
  not_found: true,
  bad_request: true,
  rate_limited: true,
  too_large: true,
  storage_full: true,
  conflict: true,
}

export type UploadOptions = {
  path: string
  blob: Blob
  filename: string
  onProgress?: (fraction: number) => void
  signal?: AbortSignal
}

export function uploadWithProgress<T>(options: UploadOptions): Promise<T> {
  const { path, blob, filename, onProgress, signal } = options
  return new Promise<T>((resolve, reject) => {
    const form = new FormData()
    form.append('file', blob, filename)

    const xhr = new XMLHttpRequest()
    xhr.open('POST', api.resolve(path), true)
    const token = api.token
    if (token) xhr.setRequestHeader('authorization', `Bearer ${token}`)

    xhr.upload.addEventListener('progress', (event) => {
      if (!event.lengthComputable) return
      onProgress?.(event.total === 0 ? 1 : event.loaded / event.total)
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(xhr.responseText ? (JSON.parse(xhr.responseText) as T) : ({} as T))
        } catch {
          reject(new ApiClientError(xhr.status, 'internal', 'invalid JSON response'))
        }
        return
      }
      let code: ApiErrorCode | 'internal' = 'internal'
      let message = `HTTP ${xhr.status}`
      try {
        const parsed: unknown = JSON.parse(xhr.responseText)
        if (typeof parsed === 'object' && parsed !== null && 'error' in parsed) {
          const error: unknown = parsed.error
          if (typeof error === 'object' && error !== null) {
            if ('message' in error && typeof error.message === 'string') message = error.message
            if ('code' in error && typeof error.code === 'string' && error.code in ERROR_CODES) {
              code = error.code as ApiErrorCode
            }
          }
        }
      } catch {
        /* non-JSON body: keep the HTTP default */
      }
      reject(new ApiClientError(xhr.status, code, message))
    })

    xhr.addEventListener('error', () => reject(new ApiClientError(0, 'network', 'upload failed')))
    xhr.addEventListener('abort', () => reject(new ApiClientError(0, 'network', 'upload aborted')))

    if (signal) {
      if (signal.aborted) {
        reject(new ApiClientError(0, 'network', 'upload aborted'))
        return
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true })
    }

    xhr.send(form)
  })
}
